import React, { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { Alert, Button, Dropdown, Form, Modal, Pagination, Spinner, Table } from "react-bootstrap";

import { crmPostventaApi } from "./crmPostventaApi";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const CONTACT_STATE_NO_RESPONSE = "No respondio";
const CONTACT_STATE_NO_RESPONSE_FALLBACK = "No respondio";
const CONTACT_STATE_NO_ANSWER = "Sin respuesta";

const CONTACT_STATE_OPTIONS = [
  "Contactado",
  CONTACT_STATE_NO_RESPONSE,
  "Derivado",
  "Cerrado",
  "Sin respuesta",
  "Otro",
];

const RESULT_OPTIONS = [
  "Sin novedades / servicio correcto",
  "Solicita rollos",
  "Consulta comercial",
  "Consulta sobre acreditación",
  "Problema técnico",
  "Solicitud de baja",
  "Datos incorrectos",
  CONTACT_STATE_NO_RESPONSE,
  "Otro",
];

const OTHER_DERIVATION_OPTIONS = [
  { value: "MAN_OTRO", label: "Otro" },
  { value: "MAN_VISITA_COMERCIAL", label: "Derivación comercial" },
  { value: "MAN_CONSULTA_GENERAL", label: "Gestión IPC / consulta" },
  { value: "MAN_RECLAMO", label: "Reclamo técnico" },
  { value: "MAN_BAJA_ALTA", label: "Retención / baja" },
  { value: "MAN_CAMBIO_CONTACTO", label: "Cambio de datos" },
];

const MOBILE_CARD_BREAKPOINT = 1100;

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatDateTime = (value) => {
  if (!value || value === "-") return "-";

  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}( \d{2}:\d{2})?$/.test(raw)) return raw;

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const splitDateTime = (value) => {
  const formatted = formatDateTime(value);
  if (formatted === "-" || !formatted.includes(" ")) {
    return { date: formatted, time: "" };
  }

  const [date, time] = formatted.split(" ");
  return { date, time: time || "" };
};

const toLocalDateTimeInputValue = (date = new Date()) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const offset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
};

const toLocalDateInputValue = (date = new Date()) => toLocalDateTimeInputValue(date).slice(0, 10);

const isNoResponseState = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized.startsWith("no respon");
};

const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");
const buildCommercePeriodKey = (item = {}) =>
  `${normalizeDigits(item?.cuit)}__${normalizeDigits(item?.numeroComercio)}__${String(item?.periodoControl || "").trim()}`;

const normalizeWhatsappPhone = (value) => {
  const digits = normalizeDigits(value);
  if (!digits) return "";
  if (digits.startsWith("549")) return digits;
  if (digits.startsWith("54")) return `549${digits.slice(2)}`;
  if (digits.startsWith("0")) return `549${digits.slice(1)}`;
  return `549${digits}`;
};

const copyText = async (text, title, successMessage = "Texto copiado correctamente.") => {
  try {
    await navigator.clipboard.writeText(text || "");
    Swal.fire(title, successMessage, "success");
  } catch {
    Swal.fire(title, "No se pudo copiar el texto.", "error");
  }
};

const buildAttemptLabel = (attempt) => `Intento ${Math.max(1, Number(attempt || 1))} de 3`;

const mapVisualState = (state) => {
  const normalized = String(state || "").trim().toLowerCase();
  if (normalized.includes("pendiente primer whatsapp")) return "Primer WhatsApp";
  if (normalized.includes("pendiente segundo whatsapp") || normalized.includes("pendiente campana")) return "Segundo WhatsApp";
  if (normalized.includes("esperando segundo contacto")) return "Esperando respuesta";
  if (normalized.includes("pendiente llamada")) return "Llamada pendiente";
  if (normalized.includes("sin respuesta")) return "Sin respuesta";
  if (normalized.includes("respondio")) return "Respondio";
  if (normalized.includes("cerrado")) return "Cerrado";
  return state || "-";
};

const resolveVisualState = (item = {}) => {
  const estado = String(item?.estado || "").trim().toLowerCase();
  const intento = Number(item?.intentoActual || 1);

  if (estado.includes("sin respuesta")) return "Sin respuesta";
  if (estado.includes("cerrado") && String(item?.ultimoZocoCheck || "-") !== "-") return "Respondio";
  if (estado.includes("cerrado")) return "Cerrado";
  if (intento <= 1) return "Primer WhatsApp";
  if (intento === 2) return item?.campania && item.campania !== "-" ? "Segundo WhatsApp" : "Esperando respuesta";
  return "Llamada pendiente";
};

const toneForPriority = (priority) => {
  const normalized = String(priority || "").toLowerCase();
  if (normalized.includes("cr")) return "danger";
  if (normalized.includes("alta")) return "warning";
  if (normalized.includes("media")) return "default";
  return "success";
};

const toneForState = (state) => {
  const normalized = String(state || "").toLowerCase();
  if (normalized.includes("respond")) return "success";
  if (normalized.includes("cerrado")) return "default";
  if (normalized.includes("sin respuesta")) return "danger";
  return "warning";
};

const buildHistoryLines = (item = {}) => {
  const lines = [];
  if (item?.intentoActual) lines.push(`Intento actual: ${buildAttemptLabel(item.intentoActual)}`);
  if (item?.ultimaGestion && item.ultimaGestion !== "-") lines.push(`Ultima gestion: ${item.ultimaGestion}`);
  if (item?.resumen) lines.push(`Resumen: ${item.resumen}`);
  return lines.length ? lines : ["Sin historial resumido disponible."];
};

const ZocoCheckPanel = ({ darkMode, rows = [], reglas = [], isAdmin = false, onRefresh }) => {
  const [filters, setFilters] = useState({
    search: "",
    priority: "",
    province: "",
    responsible: "",
    state: "",
    attempt: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [activeItem, setActiveItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [isCompactView, setIsCompactView] = useState(() => (typeof window !== "undefined" ? window.innerWidth < MOBILE_CARD_BREAKPOINT : false));
  const [form, setForm] = useState({
    fechaGestion: toLocalDateTimeInputValue(),
    estadoContacto: "",
    observacion: "",
    resultado: "",
    derivacion: "",
    evidencia: "",
    resultadoLlamada: "",
    campaniaId: "",
  });
  const scrollRef = useRef(null);

  const zocoRule = useMemo(
    () => (Array.isArray(reglas) ? reglas : []).find((item) => String(item?.nombreAccion || "").trim().toUpperCase() === "MAN_ZOCO_CHECK") || null,
    [reglas]
  );
  const visibleResultOptions = useMemo(() => RESULT_OPTIONS.filter((item) => !isNoResponseState(item)), []);
  const requiresResult = ["Contactado", "Derivado", "Otro"].includes(form.estadoContacto);
  const requiresDerivation = form.estadoContacto === "Derivado" || form.resultado === "Otro";
  const isThirdAttempt = Number(activeItem?.intentoActual || 1) === 3;

  useEffect(() => {
    if (!requiresResult && (form.resultado || form.derivacion)) {
      setForm((prev) => ({ ...prev, resultado: "", derivacion: "" }));
      return;
    }

    if (!requiresDerivation && form.derivacion) {
      setForm((prev) => ({ ...prev, derivacion: "" }));
    }
  }, [form.derivacion, form.resultado, requiresDerivation, requiresResult]);

  const uniqueRows = useMemo(() => {
    const byKey = new Map();
    (Array.isArray(rows) ? rows : []).forEach((item) => {
      const key = buildCommercePeriodKey(item);
      if (!key) return;
      const current = byKey.get(key);
      const currentHasFlow = Number(current?.idZocoCheckFlujo || 0) > 0;
      const nextHasFlow = Number(item?.idZocoCheckFlujo || 0) > 0;
      if (
        !current ||
        (!currentHasFlow && nextHasFlow) ||
        (currentHasFlow === nextHasFlow && Number(item?.scorePrioridad || 0) > Number(current?.scorePrioridad || 0))
      ) {
        byKey.set(key, item);
      }
    });
    return Array.from(byKey.values());
  }, [rows]);

  const priorityOptions = useMemo(() => Array.from(new Set(uniqueRows.map((item) => item?.prioridad).filter(Boolean))).sort(), [uniqueRows]);
  const provinceOptions = useMemo(() => Array.from(new Set(uniqueRows.map((item) => item?.provincia).filter(Boolean))).sort(), [uniqueRows]);
  const responsibleOptions = useMemo(() => Array.from(new Set(uniqueRows.map((item) => item?.responsable).filter(Boolean))).sort(), [uniqueRows]);
  const stateOptions = useMemo(() => Array.from(new Set(uniqueRows.map((item) => resolveVisualState(item)).filter(Boolean))).sort(), [uniqueRows]);

  const filteredRows = useMemo(() => {
    const needle = String(filters.search || "").trim().toLowerCase();
    return uniqueRows.filter((item) => {
      if (filters.priority && item?.prioridad !== filters.priority) return false;
      if (filters.province && item?.provincia !== filters.province) return false;
      if (filters.responsible && item?.responsable !== filters.responsible) return false;
      if (filters.state && resolveVisualState(item) !== filters.state) return false;
      if (filters.attempt && String(item?.intentoActual || "") !== filters.attempt) return false;
      if (!needle) return true;

      return [
        item?.comercio,
        item?.cuit,
        item?.numeroComercio,
        item?.provincia,
        item?.responsable,
        item?.telefono,
        item?.campania,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [filters, uniqueRows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = useMemo(() => filteredRows.slice((page - 1) * pageSize, page * pageSize), [filteredRows, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filters, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    const updateViewport = () => setIsCompactView(window.innerWidth < MOBILE_CARD_BREAKPOINT);
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [page, pageSize, filteredRows.length]);

  useEffect(() => {
    if (!activeItem || Number(activeItem?.intentoActual || 1) !== 2) return;

    let mounted = true;
    setCampaignsLoading(true);
    crmPostventaApi
      .obtenerZocoCheckCampanias()
      .then((data) => {
        if (!mounted) return;
        const items = Array.isArray(data) ? data : [];
        setCampaigns(items);
        const vigente = items.find((item) => item?.vigente) || items.find((item) => item?.activa);
        setForm((prev) => ({ ...prev, campaniaId: prev.campaniaId || String(vigente?.id || "") }));
      })
      .catch(() => {
        if (mounted) setCampaigns([]);
      })
      .finally(() => {
        if (mounted) setCampaignsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [activeItem]);

  const currentCampaign = useMemo(() => {
    const id = Number(form.campaniaId || 0);
    return campaigns.find((item) => Number(item?.id || 0) === id) || campaigns.find((item) => item?.vigente) || null;
  }, [campaigns, form.campaniaId]);

  const whatsappPhone = useMemo(() => normalizeWhatsappPhone(activeItem?.telefono), [activeItem?.telefono]);
  const suggestedMessage = useMemo(() => {
    if (!activeItem) return "";
    return Number(activeItem?.intentoActual || 1) === 2
      ? currentCampaign?.mensaje || activeItem?.mensajeSugerido || ""
      : activeItem?.mensajeSugerido || "";
  }, [activeItem, currentCampaign]);

  const clearFilters = () => {
    setFilters({
      search: "",
      priority: "",
      province: "",
      responsible: "",
      state: "",
      attempt: "",
    });
  };

  const openGestionModal = (item) => {
    if (!Number(item?.idZocoCheckFlujo || 0)) {
      Swal.fire(
        "Zoco Check",
        "Este registro no tiene un flujo Zoco Check asociado. ActualizÃƒÆ’Ã‚Â¡ la tabla o regenerÃƒÆ’Ã‚Â¡ el flujo.",
        "warning"
      );
      return;
    }

    setActiveItem(item);
    setForm({
      fechaGestion: toLocalDateTimeInputValue(),
      estadoContacto: "",
      observacion: "",
      resultado: "",
      derivacion: "",
      evidencia: "",
      resultadoLlamada: "",
      campaniaId: "",
    });
  };

  const closeGestionModal = () => {
    setActiveItem(null);
    setCampaigns([]);
  };

  const handleBootstrap = async () => {
    setBootstrapping(true);
    try {
      const data = await crmPostventaApi.bootstrapZocoCheck(toLocalDateInputValue());
      const warnings = Array.isArray(data?.warnings) && data.warnings.length ? `<br/><br/>Advertencias:<br/>${data.warnings.join("<br/>")}` : "";
      const summary = [
        `Fecha: ${data?.fechaReferencia || "-"}`,
        `Comercios evaluados: ${data?.comerciosEvaluados ?? 0}`,
        `Elegibles: ${data?.comerciosElegibles ?? 0}`,
        `Flujos creados: ${data?.flujosCreados ?? data?.comerciosCreados ?? 0}`,
        `Ya existentes: ${data?.yaExistentes ?? data?.duplicadosEvitados ?? 0}`,
        `Excluidos por facturaciÃƒÆ’Ã‚Â³n: ${data?.excluidosPorFacturacion ?? 0}`,
        `Excluidos por onboarding: ${data?.excluidosPorOnboarding ?? 0}`,
        `Excluidos por retenciÃƒÆ’Ã‚Â³n: ${data?.excluidosPorRetencion ?? 0}`,
        `Excluidos por reclamo crÃƒÆ’Ã‚Â­tico: ${data?.excluidosPorReclamoCritico ?? 0}`,
        `Legacy excluidos de la tabla: ${data?.legacyExcluidos ?? 0}`,
        `Errores/advertencias: ${data?.cantidadErrores ?? data?.warnings?.length ?? 0}`,
      ].join("<br/>");

      const created = Number(data?.flujosCreados ?? data?.comerciosCreados ?? 0);
      const icon = created > 0 ? "success" : "warning";
      await Swal.fire({
        title: "Bootstrap Zoco Check",
        html: `${data?.message || ""}<br/><br/>${summary}${warnings}`,
        icon,
      });
      if (typeof onRefresh === "function") await onRefresh();
    } catch (error) {
      Swal.fire("Bootstrap", error.message || "No se pudo ejecutar el bootstrap.", "error");
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!activeItem?.clienteId || !zocoRule?.id) {
      Swal.fire("Zoco Check", "No se encontro la regla MAN_ZOCO_CHECK para guardar la gestion.", "info");
      return;
    }

    if (!Number(activeItem?.idZocoCheckFlujo || 0)) {
      Swal.fire("Zoco Check", "Este registro no tiene un flujo Zoco Check asociado. Actualice la tabla o regenere el flujo.", "warning");
      return;
    }

    const attempt = Number(activeItem?.intentoActual || 1);
    const estadoContacto = String(form.estadoContacto || "").trim();
    const resultadoContacto = String(form.resultado || "").trim();
    const requiresResult = ["Contactado", "Derivado", "Otro"].includes(estadoContacto);
    const requiresObservation = estadoContacto === "Otro" || resultadoContacto === "Otro";
    const noResponseState = isNoResponseState(estadoContacto);
    const nextContactValue = noResponseState && attempt <= 2
      ? toLocalDateTimeInputValue(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
      : "";

    if (!estadoContacto) {
      Swal.fire("Zoco Check", "Seleccione el estado del contacto.", "info");
      return;
    }
    if (requiresResult && !resultadoContacto) {
      Swal.fire("Zoco Check", "Seleccione el resultado del contacto.", "info");
      return;
    }
    if (estadoContacto === "Derivado" && !resultadoContacto) {
      Swal.fire("Zoco Check", "Seleccione un resultado antes de derivar la gestión.", "info");
      return;
    }
    if (attempt === 2 && !form.campaniaId) {
      Swal.fire("Zoco Check", "Debe seleccionar la campaña vigente para el segundo contacto.", "info");
      return;
    }
    if (attempt === 3 && !form.resultadoLlamada.trim()) {
      Swal.fire("Zoco Check", "Debe completar el resultado de la llamada.", "info");
      return;
    }
    if (attempt === 3 && !form.evidencia.trim()) {
      Swal.fire("Zoco Check", "Debe completar la evidencia de la llamada.", "info");
      return;
    }
    if (attempt === 3 && !form.observacion.trim()) {
      Swal.fire("Zoco Check", "Debe completar la observación de la llamada.", "info");
      return;
    }
    if ((estadoContacto === "Derivado" || resultadoContacto === "Otro") && !form.derivacion) {
      Swal.fire("Zoco Check", "Seleccione un sector o derivación para continuar.", "info");
      return;
    }
    if (requiresObservation && !form.observacion.trim()) {
      Swal.fire("Zoco Check", "Debe completar la observación para este caso.", "info");
      return;
    }
    if (noResponseState && attempt >= 3) {
      Swal.fire("Zoco Check", "Después del tercer intento debe registrar el estado 'Sin respuesta'.", "info");
      return;
    }
    if (estadoContacto === CONTACT_STATE_NO_ANSWER && attempt < 3) {
      Swal.fire("Zoco Check", "El estado 'Sin respuesta' sólo está disponible después del tercer intento.", "info");
      return;
    }

    setSaving(true);
    try {
      const response = await crmPostventaApi.crearGestion(
        activeItem.clienteId,
        Number(zocoRule.id),
        estadoContacto,
        form.observacion || `Gestion Zoco Check ${buildAttemptLabel(attempt)}.`,
        form.fechaGestion,
        nextContactValue,
        "MAN_ZOCO_CHECK",
        attempt <= 2 ? "WhatsApp" : "Llamada",
        form.resultadoLlamada,
        form.evidencia,
        resultadoContacto || null,
        (estadoContacto === "Derivado" || resultadoContacto === "Otro") ? form.derivacion : "",
        form.campaniaId ? Number(form.campaniaId) : null,
        Number(activeItem.idZocoCheckFlujo),
        activeItem?.seguimientoPrincipalId ? Number(activeItem.seguimientoPrincipalId) : null,
        estadoContacto,
        resultadoContacto || null
      );

      await Swal.fire({
        title: "Zoco Check",
        html: `${response?.message || "Gestion guardada correctamente."}${response?.gestionDerivadaId ? `<br/><br/>Gestion derivada creada: #${response.gestionDerivadaId}` : ""}`,
        icon: "success",
      });
      closeGestionModal();
      if (typeof onRefresh === "function") await onRefresh();
    } catch (error) {
      Swal.fire("Zoco Check", error.message || "No se pudo guardar la gestion.", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderFilters = () => (
    <div className="crm-postventa-zoco-filters-wrap">
      <div className="crm-postventa-zoco-filter-grid crm-postventa-zoco-filter-grid-top">
        <Form.Group>
          <Form.Label>Buscar</Form.Label>
          <Form.Control
            type="search"
            className="crm-postventa-filter-control"
            placeholder="Comercio, CUIT, numero o responsable"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Prioridad</Form.Label>
          <Form.Select className="crm-postventa-filter-control" value={filters.priority} onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}>
            <option value="">Todas</option>
            {priorityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Provincia</Form.Label>
          <Form.Select className="crm-postventa-filter-control" value={filters.province} onChange={(event) => setFilters((prev) => ({ ...prev, province: event.target.value }))}>
            <option value="">Todas</option>
            {provinceOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Responsable</Form.Label>
          <Form.Select className="crm-postventa-filter-control" value={filters.responsible} onChange={(event) => setFilters((prev) => ({ ...prev, responsible: event.target.value }))}>
            <option value="">Todos</option>
            {responsibleOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </Form.Select>
        </Form.Group>
      </div>
      <div className="crm-postventa-zoco-filter-grid crm-postventa-zoco-filter-grid-bottom">
        <Form.Group>
          <Form.Label>Estado</Form.Label>
          <Form.Select className="crm-postventa-filter-control" value={filters.state} onChange={(event) => setFilters((prev) => ({ ...prev, state: event.target.value }))}>
            <option value="">Todos</option>
            {stateOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Intento</Form.Label>
          <Form.Select className="crm-postventa-filter-control" value={filters.attempt} onChange={(event) => setFilters((prev) => ({ ...prev, attempt: event.target.value }))}>
            <option value="">Todos</option>
            <option value="1">Intento 1</option>
            <option value="2">Intento 2</option>
            <option value="3">Intento 3</option>
          </Form.Select>
        </Form.Group>
        <div className="crm-postventa-zoco-filter-actions">
          <Button variant="outline-secondary" onClick={clearFilters}>Limpiar filtros</Button>
        </div>
        <div className="crm-postventa-zoco-filter-actions crm-postventa-zoco-filter-actions-end">
          {isAdmin ? (
            <Button size="sm" variant="outline-success" onClick={handleBootstrap} disabled={bootstrapping}>
              {bootstrapping ? "Generando..." : "Generar Zoco Check del mes"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderActions = (item) => {
    const whatsapp = normalizeWhatsappPhone(item?.telefono);
    const canClose = Number(item?.intentoActual || 1) >= 3;

    return (
      <div className="crm-postventa-zoco-actions">
        <Button size="sm" variant="outline-secondary" onClick={() => setDetailItem(item)}>
          Detalle
        </Button>
        {whatsapp ? (
          <Button size="sm" variant="outline-success" href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
            WhatsApp
          </Button>
        ) : (
          <Button size="sm" variant="outline-secondary" disabled>
            WhatsApp
          </Button>
        )}
        <Dropdown align="end">
          <Dropdown.Toggle size="sm" variant="outline-secondary" className="crm-postventa-zoco-menu-toggle">
            ...
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => openGestionModal(item)}>Abrir gestion</Dropdown.Item>
            <Dropdown.Item onClick={() => setDetailItem(item)}>Ver historial</Dropdown.Item>
            <Dropdown.Item onClick={() => copyText(item?.telefono || "", "Telefono", "Telefono copiado correctamente.")} disabled={!item?.telefono}>
              Copiar telefono
            </Dropdown.Item>
            {canClose ? <Dropdown.Item onClick={() => openGestionModal(item)}>Cerrar</Dropdown.Item> : null}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  const renderDesktopTable = () => (
    <div className="crm-postventa-zoco-table-shell">
      <div ref={scrollRef} className="crm-postventa-zoco-table-scroll">
        <Table className="crm-postventa-table crm-postventa-zoco-compact-table mb-0" hover>
          <colgroup>
            <col style={{ width: "90px" }} />
            <col style={{ width: "220px" }} />
            <col style={{ width: "115px" }} />
            <col style={{ width: "110px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "125px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "155px" }} />
            <col style={{ width: "145px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "170px" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Prioridad</th>
              <th>Comercio</th>
              <th>CUIT</th>
              <th>N° comercio</th>
              <th className="crm-postventa-zoco-col-hide-1400">Provincia</th>
              <th>Facturación 30d</th>
              <th className="crm-postventa-zoco-col-hide-1400">Operaciones</th>
              <th>Intento</th>
              <th>Estado</th>
              <th>Próximo contacto</th>
              <th className="crm-postventa-zoco-col-hide-1400">Responsable</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? pageRows.map((item, index) => {
              const nextContact = splitDateTime(item?.proximoContacto);
              const fullState = resolveVisualState(item);
              const shortState = mapVisualState(fullState);
              return (
                <tr key={`${buildCommercePeriodKey(item)}-${index}`}>
                  <td className="crm-postventa-cell-nowrap">
                    <span className={`crm-postventa-badge tone-${toneForPriority(item?.prioridad)}`}>{item?.prioridad || "-"}</span>
                  </td>
                  <td className="crm-postventa-zoco-main-cell">
                    <strong title={item?.comercio || "-"}>{item?.comercio || "-"}</strong>
                    <span title={item?.telefono || "-"}>{item?.telefono || "-"}</span>
                  </td>
                  <td className="crm-postventa-cell-nowrap">{item?.cuit || "-"}</td>
                  <td className="crm-postventa-cell-nowrap">{item?.numeroComercio || "-"}</td>
                  <td className="crm-postventa-cell-ellipsis crm-postventa-zoco-col-hide-1400" title={item?.provincia || "-"}>{item?.provincia || "-"}</td>
                  <td className="crm-postventa-cell-nowrap">{formatCurrency(item?.facturacion30Dias)}</td>
                  <td className="crm-postventa-cell-nowrap crm-postventa-zoco-col-hide-1400">{item?.cantidadOperaciones30Dias || 0}</td>
                  <td className="crm-postventa-cell-nowrap">{buildAttemptLabel(item?.intentoActual)}</td>
                  <td className="crm-postventa-cell-nowrap">
                    <span
                      className={`crm-postventa-badge crm-postventa-zoco-state-badge tone-${toneForState(shortState)}`}
                      title={fullState}
                    >
                      {shortState}
                    </span>
                  </td>
                  <td className="crm-postventa-cell-nowrap">
                    <div className="crm-postventa-zoco-date-cell" title={formatDateTime(item?.proximoContacto)}>
                      <span>{nextContact.date || "-"}</span>
                      {nextContact.time ? <small>{nextContact.time}</small> : null}
                    </div>
                  </td>
                  <td className="crm-postventa-cell-ellipsis crm-postventa-zoco-col-hide-1400" title={item?.responsable || "-"}>{item?.responsable || "-"}</td>
                  <td>{renderActions(item)}</td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={12} className="text-center py-4 text-muted">
                  No hay Zoco Check visibles para los filtros actuales.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );

  const renderCompactCards = () => (
    <div className="crm-postventa-zoco-card-list">
      {pageRows.length ? pageRows.map((item, index) => {
        const nextContact = splitDateTime(item?.proximoContacto);
        const fullState = resolveVisualState(item);
        const shortState = mapVisualState(fullState);
        return (
          <article key={`${buildCommercePeriodKey(item)}-${index}`} className="crm-postventa-zoco-card">
            <div className="crm-postventa-zoco-card-head">
              <div>
                <strong title={item?.comercio || "-"}>{item?.comercio || "-"}</strong>
                <span>{item?.telefono || "-"}</span>
              </div>
              <span className={`crm-postventa-badge tone-${toneForPriority(item?.prioridad)}`}>{item?.prioridad || "-"}</span>
            </div>
            <div className="crm-postventa-zoco-card-meta">
              <div>
                <span className="crm-postventa-card-label">Estado</span>
                <strong title={fullState}>{shortState}</strong>
              </div>
              <div>
                <span className="crm-postventa-card-label">Próximo contacto</span>
                <strong>{nextContact.date || "-"}</strong>
                {nextContact.time ? <small>{nextContact.time}</small> : null}
              </div>
              <div>
                <span className="crm-postventa-card-label">CUIT / comercio</span>
                <strong>{item?.cuit || "-"}</strong>
                <small>{item?.numeroComercio || "-"}</small>
              </div>
            </div>
            <div className="crm-postventa-zoco-card-actions">
              {renderActions(item)}
            </div>
          </article>
        );
      }) : (
        <div className="text-center py-4 text-muted">No hay Zoco Check visibles para los filtros actuales.</div>
      )}
    </div>
  );

  const renderPagination = () => (
    <div className="crm-postventa-zoco-footer">
      <div className="crm-postventa-zoco-footer-meta">
        <Form.Select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
          {PAGE_SIZE_OPTIONS.map((item) => <option key={item} value={item}>{item} por página</option>)}
        </Form.Select>
      </div>
      <div className="crm-postventa-zoco-footer-pagination">
        <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
          Anterior
        </Button>
        <span>Página {page} de {totalPages}</span>
        <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
          Siguiente
        </Button>
      </div>
    </div>
  );

  return (
    <section className={`crm-postventa-panel crm-postventa-alert-queue ${darkMode ? "is-dark" : ""}`}>
      <div className="crm-postventa-panel-title">
        <div>
          <h3>Zoco Check</h3>
          <span className="crm-postventa-card-label">
            Mostrando {pageRows.length} de {filteredRows.length} Zoco Check
          </span>
        </div>
      </div>

      {renderFilters()}
      {isCompactView ? renderCompactCards() : renderDesktopTable()}
      {renderPagination()}

      <Modal show={Boolean(detailItem)} onHide={() => setDetailItem(null)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Detalle Zoco Check · {detailItem?.comercio || "-"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-grid gap-3">
          <div className="crm-postventa-zoco-detail-grid">
            <div><span className="crm-postventa-card-label">Terminales activas</span><strong>{detailItem?.cantidadTerminalesActivas || 0}</strong></div>
            <div><span className="crm-postventa-card-label">Última venta</span><strong>{formatDateTime(detailItem?.ultimaActividad)}</strong></div>
            <div><span className="crm-postventa-card-label">Facturación 30 días</span><strong>{formatCurrency(detailItem?.facturacion30Dias)}</strong></div>
            <div><span className="crm-postventa-card-label">Promedio diario</span><strong>{formatCurrency(detailItem?.promedioDiario30Dias)}</strong></div>
            <div><span className="crm-postventa-card-label">Operaciones</span><strong>{detailItem?.cantidadOperaciones30Dias || 0}</strong></div>
            <div><span className="crm-postventa-card-label">Última entrega de rollos</span><strong>{formatDateTime(detailItem?.ultimaEntregaRollos)}</strong></div>
            <div><span className="crm-postventa-card-label">Último Zoco Check</span><strong>{formatDateTime(detailItem?.ultimoZocoCheck)}</strong></div>
            <div><span className="crm-postventa-card-label">Campaña</span><strong>{detailItem?.campania || "-"}</strong></div>
            <div><span className="crm-postventa-card-label">Mensaje utilizado</span><strong>{detailItem?.mensajeSugerido || "-"}</strong></div>
            <div className="crm-postventa-zoco-detail-wide"><span className="crm-postventa-card-label">Motivo de prioridad</span><strong>{detailItem?.motivoPrioridad || "-"}</strong></div>
            <div className="crm-postventa-zoco-detail-wide">
              <span className="crm-postventa-card-label">Historial de intentos</span>
              <ul className="crm-postventa-detail-lines">
                {buildHistoryLines(detailItem).map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDetailItem(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(activeItem)} onHide={closeGestionModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Zoco Check · {activeItem?.comercio || "-"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body className="d-grid gap-3">
            <div className="crm-postventa-zoco-modal-grid">
              <div><span className="crm-postventa-card-label">Comercio</span><strong>{activeItem?.comercio || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">CUIT</span><strong>{activeItem?.cuit || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Teléfono</span><strong>{activeItem?.telefono || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">N° comercio</span><strong>{activeItem?.numeroComercio || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Última venta</span><strong>{formatDateTime(activeItem?.ultimaActividad)}</strong></div>
              <div><span className="crm-postventa-card-label">Facturación 30d</span><strong>{formatCurrency(activeItem?.facturacion30Dias)}</strong></div>
              <div><span className="crm-postventa-card-label">Terminales activas</span><strong>{activeItem?.cantidadTerminalesActivas || 0}</strong></div>
              <div><span className="crm-postventa-card-label">Última entrega rollos</span><strong>{formatDateTime(activeItem?.ultimaEntregaRollos)}</strong></div>
              <div><span className="crm-postventa-card-label">Último Zoco Check</span><strong>{formatDateTime(activeItem?.ultimoZocoCheck)}</strong></div>
              <div><span className="crm-postventa-card-label">Prioridad / motivo</span><strong>{activeItem?.prioridad || "-"} · {activeItem?.motivoPrioridad || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Intento actual</span><strong>{buildAttemptLabel(activeItem?.intentoActual)}</strong></div>
              <div><span className="crm-postventa-card-label">Próxima acción obligatoria</span><strong>{activeItem?.accionSugerida || "-"}</strong></div>
              <div><span className="crm-postventa-card-label">Campaña vigente</span><strong>{currentCampaign?.nombreCampania || activeItem?.campania || "-"}</strong></div>
            </div>

            <Alert variant="light" className="mb-0">
              <div><strong>Detalle técnico</strong></div>
              <div>Flujo Zoco Check: #{activeItem?.idZocoCheckFlujo || "-"}</div>
              <div>Período: {activeItem?.periodoControl || "-"}</div>
              <div>Intento: {buildAttemptLabel(activeItem?.intentoActual)}</div>
              <div>Seguimiento principal: #{activeItem?.seguimientoPrincipalId || "-"}</div>
            </Alert>

            <div className="crm-postventa-zoco-message">
              <span className="crm-postventa-card-label">Mensaje sugerido</span>
              <div className="crm-postventa-detail-pre">{suggestedMessage || "-"}</div>
            </div>

            {Number(activeItem?.intentoActual || 1) === 2 ? (
              <Form.Group>
                <Form.Label>Campaña vigente</Form.Label>
                {campaignsLoading ? (
                  <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando campañas...</div>
                ) : campaigns.length ? (
                  <Form.Select value={form.campaniaId} onChange={(event) => setForm((prev) => ({ ...prev, campaniaId: event.target.value }))}>
                    <option value="">Seleccionar campaña</option>
                    {campaigns.map((item) => (
                      <option key={item.id} value={item.id}>{item.nombreCampania} Ãƒâ€šÃ‚Â· {item.estado}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <Alert variant="warning" className="mb-0">No hay campañas vigentes o activas para este segundo contacto.</Alert>
                )}
              </Form.Group>
            ) : null}

            <div className="crm-postventa-zoco-cta-row">
              {suggestedMessage ? (
                <Button type="button" variant="outline-secondary" onClick={() => copyText(suggestedMessage, "Zoco Check")}>
                  Copiar mensaje
                </Button>
              ) : null}
              {whatsappPhone ? (
                <Button type="button" variant="outline-success" href={`https://wa.me/${whatsappPhone}`} target="_blank" rel="noopener noreferrer">
                  Abrir WhatsApp
                </Button>
              ) : null}
            </div>

            <div className="crm-postventa-form-grid">
              <Form.Group className="crm-postventa-form-grid-span-2">
                <Form.Label>Estado del contacto</Form.Label>
                <Form.Select value={form.estadoContacto} onChange={(event) => setForm((prev) => ({ ...prev, estadoContacto: event.target.value }))}>
                  <option value="">Seleccionar</option>
                  {CONTACT_STATE_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
                </Form.Select>
              </Form.Group>
              {requiresResult ? (
                <Form.Group>
                  <Form.Label>Resultado del contacto</Form.Label>
                  <Form.Select value={form.resultado} onChange={(event) => setForm((prev) => ({ ...prev, resultado: event.target.value }))}>
                    <option value="">Seleccionar</option>
                    {visibleResultOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </Form.Group>
              ) : null}
              {requiresDerivation ? (
                <Form.Group>
                  <Form.Label>Sector / derivación</Form.Label>
                  <Form.Select value={form.derivacion} onChange={(event) => setForm((prev) => ({ ...prev, derivacion: event.target.value }))}>
                    <option value="">Seleccionar</option>
                    {OTHER_DERIVATION_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </Form.Select>
                </Form.Group>
              ) : null}
              <Form.Group>
                <Form.Label>Fecha / hora</Form.Label>
                <Form.Control type="datetime-local" value={form.fechaGestion} onChange={(event) => setForm((prev) => ({ ...prev, fechaGestion: event.target.value }))} />
              </Form.Group>
              {isThirdAttempt ? (
                <>
                  <Form.Group>
                    <Form.Label>Resultado de la llamada</Form.Label>
                    <Form.Control value={form.resultadoLlamada} onChange={(event) => setForm((prev) => ({ ...prev, resultadoLlamada: event.target.value }))} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Evidencia</Form.Label>
                    <Form.Control value={form.evidencia} onChange={(event) => setForm((prev) => ({ ...prev, evidencia: event.target.value }))} />
                  </Form.Group>
                </>
              ) : null}
              <Form.Group className="crm-postventa-form-grid-span-2">
                <Form.Label>Observación</Form.Label>
                <Form.Control as="textarea" rows={4} value={form.observacion} onChange={(event) => setForm((prev) => ({ ...prev, observacion: event.target.value }))} />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={closeGestionModal}>Cancelar</Button>
            <Button type="submit" variant="success" disabled={saving}>{saving ? "Guardando..." : "Guardar gestión"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </section>
  );
};

export default ZocoCheckPanel;


